import { useCallback, useMemo, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Tooltip } from '@mui/material'
import { CallEnd, VideoCall, Videocam } from '@mui/icons-material'
import { getHasOngoingCall, getIsCurrentUserPresenter } from '@tabeeb/modules/shared/content/selectors'
import { leaveVideoCall } from '@tabeeb/modules/shared/content/actions'
import { notifyStartCall } from '@tabeeb/modules/presentation/actions/connection'
import {
  getIsCallInitializationInProgress,
  getIsCallLeavingInProgress,
  getIsInPresentationMode,
} from '@tabeeb/modules/presentation/selectors'
import { getIsVideoCallsEnabled } from '@tabeeb/modules/appConfigState/selectors'
import { usePopoverState } from '@collaborate-health/uikit'
import CallRingtoneManager from '@tabeeb/services/callRingtoneManager'
import { getHasStartCallPermission } from '../../selectors'

const StartCallButton = () => {
  const dispatch = useDispatch()
  const [, open, onOpen, onClose] = usePopoverState()
  const tooltipTimerRef = useRef(null)
  const tooltipTimeout = 7000

  const isPresenter = useSelector(getIsCurrentUserPresenter)
  const isCallStarted = useSelector(getIsInPresentationMode)
  const isCallInitializationInProgress = useSelector(getIsCallInitializationInProgress)
  const isCallLeavingInProgress = useSelector(getIsCallLeavingInProgress)
  const hasOngoingCall = useSelector(getHasOngoingCall)
  const hasStartCallPermission = useSelector(getHasStartCallPermission)
  const isVideoCallsFeatureEnabled = useSelector(getIsVideoCallsEnabled)

  const onJoinCall = useCallback(() => {
    onClose()

    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }

    dispatch(notifyStartCall())
    CallRingtoneManager.getInstance().reset()
  }, [dispatch, onClose])

  const onLeaveCall = useCallback(() => {
    dispatch(leaveVideoCall({ force: false }))
  }, [dispatch])

  useEffect(() => {
    if (hasOngoingCall && !isPresenter && !isCallInitializationInProgress && !isCallStarted) {
      onOpen()
      tooltipTimerRef.current = setTimeout(() => {
        onClose()
      }, tooltipTimeout)
    }
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
        tooltipTimerRef.current = null
      }

      onClose()
    }
  }, [hasOngoingCall, onOpen, onClose, isPresenter, isCallInitializationInProgress, isCallStarted])

  const buttonProps = useMemo(() => {
    if (isCallStarted) {
      return {
        color: 'error',
        disabled: isCallLeavingInProgress,
        startIcon: <CallEnd />,
        title: isPresenter ? 'End call' : 'Leave call',
        onClick: onLeaveCall,
      }
    }

    if (!hasOngoingCall && !hasStartCallPermission) {
      return null
    }

    return {
      color: 'primary',
      disabled: isCallInitializationInProgress,
      startIcon: hasOngoingCall ? <VideoCall /> : <Videocam />,
      title: hasOngoingCall ? 'Join' : 'Start call',
      onClick: onJoinCall,
    }
  }, [
    hasOngoingCall,
    hasStartCallPermission,
    isCallInitializationInProgress,
    isCallLeavingInProgress,
    isCallStarted,
    isPresenter,
    onJoinCall,
    onLeaveCall,
  ])

  if (!buttonProps || !isVideoCallsFeatureEnabled) {
    return null
  }

  return (
    <Tooltip arrow open={open} title='You can join the call!' placement='bottom'>
      <Button variant='contained' {...buttonProps} title={undefined}>
        {buttonProps.title}
      </Button>
    </Tooltip>
  )
}

export default StartCallButton
